/**
 * Entity Registry - Registro global de entidades
 *
 * @version 1.0
 * @module core/entity-registry
 *
 * @description
 * Registry centralizado donde cada módulo registra sus tipos de entidades
 * con toda su configuración (API, UI, factory, etc.)
 */

import type { EntityConfig, EntityRegistry } from './types';

/**
 * Registry global de entidades
 * @private
 */
const registry: EntityRegistry = new Map();

/**
 * Registra una configuración de entidad
 *
 * @param config - Configuración completa de la entidad
 *
 * @example
 * ```ts
 * registerEntity({
 *   type: 'tree',
 *   displayName: 'árbol',
 *   toolId: 'create-tree',
 *   apiService: treesApi,
 *   detailsPanel: TreeDetailsPanel,
 *   icon: TreeDeciduous,
 *   createDefault: (coords) => ({ ...defaults }),
 *   tempIdPrefix: 'tree-',
 *   codePrefix: 'TR',
 * });
 * ```
 */
export function registerEntity<T>(config: EntityConfig<T>): void {
  if (registry.has(config.type)) {
    console.warn(`[EntityRegistry] Entidad '${config.type}' ya registrada. Sobrescribiendo...`);
  }

  // Agregar funciones por defecto si no están definidas
  const completeConfig: EntityConfig<T> = {
    ...config,
    getId: config.getId || ((entity: any) => entity.id),
    getCoordinates: config.getCoordinates || ((entity: any) => entity.coordinates),
    getCode: config.getCode || ((entity: any) => entity.code),
  };

  registry.set(config.type, completeConfig);
  console.log(`[EntityRegistry] ✓ Entidad registrada: ${config.type} (${config.displayName})`);
}

/**
 * Obtiene la configuración de una entidad por su tipo
 *
 * @param type - Tipo de entidad
 * @returns Configuración de la entidad o undefined si no existe
 *
 * @example
 * ```ts
 * const config = getEntityConfig('tree');
 * if (config) {
 *   const tree = config.createDefault([lng, lat]);
 * }
 * ```
 */
export function getEntityConfig<T = any>(type: string): EntityConfig<T> | undefined {
  return registry.get(type) as EntityConfig<T> | undefined;
}

/**
 * Obtiene la configuración de una entidad por el ID de su herramienta
 *
 * @param toolId - ID de la herramienta del toolbar
 * @returns Configuración de la entidad o undefined si no existe
 *
 * @example
 * ```ts
 * const config = getEntityConfigByToolId('create-tree');
 * // config.type === 'tree'
 * ```
 */
export function getEntityConfigByToolId<T = any>(toolId: string): EntityConfig<T> | undefined {
  for (const config of registry.values()) {
    if (config.toolId === toolId) {
      return config as EntityConfig<T>;
    }
  }
  return undefined;
}

/**
 * Obtiene todas las configuraciones registradas
 *
 * @returns Array con todas las configuraciones
 */
export function getAllEntityConfigs(): EntityConfig[] {
  return Array.from(registry.values());
}

/**
 * Verifica si una entidad está registrada
 *
 * @param type - Tipo de entidad
 * @returns true si está registrada
 */
export function isEntityRegistered(type: string): boolean {
  return registry.has(type);
}

/**
 * Limpia el registry (útil para testing)
 * @internal
 */
export function clearRegistry(): void {
  registry.clear();
  console.log('[EntityRegistry] Registry limpiado');
}
